import React from 'react';
import { useNavigate } from 'react-router-dom';
import './InvoiceDisplay.css';

interface InvoiceDisplayProps {
  registration: {
    id: string;
    primaryCamper: {
      firstName: string;
      lastName: string;
      email: string;
      address?: string;
      city?: string;
      province?: string;
      postalCode?: string;
    };
    accommodationFees: {
      placeName: string;
      placeCode: string;
      fee: number;
      numberOfNights: number;
      override?: {
        amount: number;
        note: string;
      };
    };
    registrationFees: Array<{
      name: string;
      category: string;
      nights: number;
      fee: number;
    }>;
    registrationOverride?: {
      amount: number;
      note: string;
    };
    mealFees?: {
      fullCampAdult?: { quantity: number; unitPrice: number; total: number };
      fullCampChild?: { quantity: number; unitPrice: number; total: number };
      dailyAdult?: { quantity: number; unitPrice: number; total: number };
      dailyChild?: { quantity: number; unitPrice: number; total: number };
      override?: {
        amount: number;
        note: string;
      };
    };
    otherFees: {
      fridgeRental: number;
      donation: number;
      donationNote?: string;
      customFee?: number;
      customFeeNote?: string;
      otherFees?: Array<{
        id: string;
        amount: number;
        note: string;
      }>;
      override?: {
        amount: number;
        note: string;
      };
    };
    totalAmount: number;
    paymentAmount: number;
    remainingBalance: number;
  };
  invoiceNumber: string;
  invoiceDate?: Date | string | null;
  containerId?: string;
}

const InvoiceDisplay: React.FC<InvoiceDisplayProps> = ({
  registration,
  invoiceNumber,
  invoiceDate,
  containerId = 'invoice-container',
}) => {
  const navigate = useNavigate();
  console.log('InvoiceDisplay Props:', {
    accommodationFees: registration?.accommodationFees,
    accommodationTotal: registration?.accommodationFees ? (
      registration.accommodationFees.fee + 
      (registration.accommodationFees.override?.amount || 0)
    ) : 0
  });
  console.log('InvoiceDisplay RENDERING - Footer should be visible!');

  if (!registration || !registration.accommodationFees) {
    console.log('Invalid invoice data:', { registration });
    return (
      <div className="invoice-container">
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h2>Error: Invalid invoice data</h2>
          <p>The invoice data is incomplete or invalid.</p>
        </div>
      </div>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount);
  };

  const handlePrint = () => {
    const inModal = Boolean(document.querySelector('.modal.show'));
    document.body.classList.add('printing-invoice');
    if (inModal) {
      document.body.classList.add('printing-invoice-modal');
    }

    const cleanup = () => {
      document.body.classList.remove('printing-invoice', 'printing-invoice-modal');
      window.removeEventListener('afterprint', cleanup);
    };

    window.addEventListener('afterprint', cleanup);
    window.print();
  };

  // Calculate section totals including overrides
  const accommodationFee = Number(registration.accommodationFees.fee) || 0;
  const accommodationOverride = Number(registration.accommodationFees.override?.amount) || 0;
  const accommodationTotal = accommodationFee + accommodationOverride;

  const registrationBaseTotal = registration.registrationFees.reduce(
    (sum, fee) => sum + (Number(fee.fee) || 0), 0
  );
  const registrationOverride = Number(registration.registrationOverride?.amount) || 0;
  const registrationTotal = registrationBaseTotal + registrationOverride;

  // Meal fees calculation
  const mealFees = registration.mealFees;
  const mealFullCampAdultTotal = (mealFees?.fullCampAdult?.quantity || 0) * (mealFees?.fullCampAdult?.unitPrice || 0);
  const mealFullCampChildTotal = (mealFees?.fullCampChild?.quantity || 0) * (mealFees?.fullCampChild?.unitPrice || 0);
  const mealDailyAdultTotal = (mealFees?.dailyAdult?.quantity || 0) * (mealFees?.dailyAdult?.unitPrice || 0);
  const mealDailyChildTotal = (mealFees?.dailyChild?.quantity || 0) * (mealFees?.dailyChild?.unitPrice || 0);
  const mealFeesOverride = Number(mealFees?.override?.amount) || 0;
  const mealFeesBaseTotal = mealFullCampAdultTotal + mealFullCampChildTotal + mealDailyAdultTotal + mealDailyChildTotal;
  const mealFeesTotal = mealFeesBaseTotal + mealFeesOverride;

  const fridgeRental = Number(registration.otherFees.fridgeRental) || 0;
  const donation = Number(registration.otherFees.donation) || 0;
  const customFee = Number(registration.otherFees.customFee) || 0;
  const otherFeesArrayTotal = (registration.otherFees.otherFees || []).reduce((sum, fee) => sum + (Number(fee.amount) || 0), 0);
  const otherFeesOverride = Number(registration.otherFees.override?.amount) || 0;
  const otherFeesBaseTotal = fridgeRental + donation + customFee + otherFeesArrayTotal;
  const otherFeesTotal = otherFeesBaseTotal + otherFeesOverride;

  // Fix subtotal calculation to account for overrides
  const subtotal = accommodationTotal + registrationTotal + mealFeesTotal + otherFeesTotal;

  // Calculate balance due as subtotal minus payments made
  const paymentAmount = Number(registration.paymentAmount) || 0;
  const balanceDue = subtotal - paymentAmount;

  return (
    <div className="invoice-container" id={containerId}>
      {/* Header */}
      <div className="invoice-header">
        <div className="logo-section">
          <div className="logo-with-text">
            <img 
              src="/images/berwick-logo.png" 
              alt="Berwick Camp Logo" 
              className="camp-logo"
            />
            <div className="organization-name">
              <h1>UCCMA</h1>
              <h2>United Church Camp Meeting Association</h2>
            </div>
          </div>
        </div>
        <div className="invoice-info">
          <h2>Invoice</h2>
          <p>Invoice #: {invoiceNumber}</p>
          <p>Date: {invoiceDate ? (typeof invoiceDate === 'string' && invoiceDate.includes('-') && invoiceDate.split('-').length === 3 ? invoiceDate : (typeof invoiceDate === 'string' ? new Date(invoiceDate).toLocaleDateString() : invoiceDate.toLocaleDateString())) : new Date().toLocaleDateString()}</p>
          <button onClick={handlePrint} className="print-button">
            <span>🖨️</span> Print Invoice
          </button>
        </div>
      </div>

      {/* Billing Information */}
      <div className="billing-info">
        <div className="bill-to">
          <h3>Bill To:</h3>
          <p>
            {registration.primaryCamper.firstName || ''} {registration.primaryCamper.lastName || ''}
            {registration.primaryCamper.address && `, ${registration.primaryCamper.address}`}
            {registration.primaryCamper.city && `, ${registration.primaryCamper.city}`}
            {registration.primaryCamper.province && `, ${registration.primaryCamper.province}`}
            {registration.primaryCamper.postalCode && ` ${registration.primaryCamper.postalCode}`}
          </p>
          <p>{registration.primaryCamper.email}</p>
        </div>
      </div>

      {/* Fees Sections */}
      <div className="fees-section">
        <h3>Accommodation Fees:</h3>
        <table className="fees-table">
          <tbody>
            <tr>
              <td>{registration.accommodationFees.placeName} - {registration.accommodationFees.numberOfNights} nights</td>
              <td>{formatCurrency(registration.accommodationFees.fee)}</td>
            </tr>
            {registration.accommodationFees.override && (
              <tr className="override-row">
                <td>Accommodation Override{registration.accommodationFees.override.note && ` (${registration.accommodationFees.override.note})`}</td>
                <td>{formatCurrency(registration.accommodationFees.override.amount)}</td>
              </tr>
            )}
            <tr className="section-total">
              <td>Total Accommodation Fees:</td>
              <td>{formatCurrency(accommodationTotal)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {(registration.registrationFees.length > 0 || !!registration.registrationOverride) && (
        <div className="fees-section">
          <h3>Registration Fees:</h3>
          <table className="fees-table">
            <tbody>
              {registration.registrationFees.map((fee, index) => (
                <tr key={index}>
                  <td>{fee.name} ({fee.category}) - {fee.nights} nights</td>
                  <td>{formatCurrency(fee.fee)}</td>
                </tr>
              ))}
              {registration.registrationOverride && (
                <tr className="override-row">
                  <td>Registration Override{registration.registrationOverride.note && ` (${registration.registrationOverride.note})`}</td>
                  <td>{formatCurrency(registration.registrationOverride.amount)}</td>
                </tr>
              )}
              <tr className="section-total">
                <td>Total Registration Fees:</td>
                <td>{formatCurrency(registrationTotal)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {mealFeesTotal > 0 && (
        <div className="fees-section">
          <h3>Meal Fees:</h3>
          <table className="fees-table">
            <tbody>
              {mealFees?.fullCampAdult && mealFees.fullCampAdult.quantity > 0 && (
                <tr>
                  <td>Full Camp Pass - Adult x{mealFees.fullCampAdult.quantity}</td>
                  <td>{formatCurrency(mealFullCampAdultTotal)}</td>
                </tr>
              )}
              {mealFees?.fullCampChild && mealFees.fullCampChild.quantity > 0 && (
                <tr>
                  <td>Full Camp Pass - Child x{mealFees.fullCampChild.quantity}</td>
                  <td>{formatCurrency(mealFullCampChildTotal)}</td>
                </tr>
              )}
              {mealFees?.dailyAdult && mealFees.dailyAdult.quantity > 0 && (
                <tr>
                  <td>Daily Pass - Adult x{mealFees.dailyAdult.quantity}</td>
                  <td>{formatCurrency(mealDailyAdultTotal)}</td>
                </tr>
              )}
              {mealFees?.dailyChild && mealFees.dailyChild.quantity > 0 && (
                <tr>
                  <td>Daily Pass - Child x{mealFees.dailyChild.quantity}</td>
                  <td>{formatCurrency(mealDailyChildTotal)}</td>
                </tr>
              )}
              {mealFees?.override && (
                <tr className="override-row">
                  <td>Meal Fees Override{mealFees.override.note && ` (${mealFees.override.note})`}</td>
                  <td>{formatCurrency(mealFees.override.amount)}</td>
                </tr>
              )}
              <tr className="section-total">
                <td>Total Meal Fees:</td>
                <td>{formatCurrency(mealFeesTotal)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {otherFeesTotal > 0 && (
        <div className="fees-section">
          <h3>Other Fees:</h3>
          <table className="fees-table">
            <tbody>
              {registration.otherFees.fridgeRental > 0 && (
                <tr>
                  <td>Fridge Rental</td>
                  <td>{formatCurrency(registration.otherFees.fridgeRental)}</td>
                </tr>
              )}
              {registration.otherFees.donation > 0 && (
                <tr>
                  <td>Donation to Camp{registration.otherFees.donationNote && ` (${registration.otherFees.donationNote})`}</td>
                  <td>{formatCurrency(registration.otherFees.donation)}</td>
                </tr>
              )}
              {customFee > 0 && (
                <tr>
                  <td>Other Fees{registration.otherFees.customFeeNote && ` (${registration.otherFees.customFeeNote})`}</td>
                  <td>{formatCurrency(customFee)}</td>
                </tr>
              )}
              {registration.otherFees.otherFees && registration.otherFees.otherFees.map((fee, index) => (
                <tr key={fee.id}>
                  <td>{fee.note}</td>
                  <td>{formatCurrency(fee.amount)}</td>
                </tr>
              ))}
              {registration.otherFees.override && (
                <tr className="override-row">
                  <td>Other Fees Override{registration.otherFees.override.note && ` (${registration.otherFees.override.note})`}</td>
                  <td>{formatCurrency(registration.otherFees.override.amount)}</td>
                </tr>
              )}
              <tr className="section-total">
                <td>Total Other Fees:</td>
                <td>{formatCurrency(otherFeesTotal)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Totals */}
      <div className="invoice-totals">
        <div className="total-line">
          <span>Subtotal:</span>
          <span>{formatCurrency(subtotal)}</span>
        </div>
        <div className="total-line">
          <span>Payments Made:</span>
          <span>-{formatCurrency(paymentAmount)}</span>
        </div>
        <div className="total-line balance">
          <span>Balance remaining:</span>
          <span>{formatCurrency(balanceDue)}</span>
        </div>
      </div>

      <div style={{
        marginTop: '30px',
        padding: '20px',
        backgroundColor: '#f8f9fa',
        border: '1px solid #dee2e6',
        borderRadius: '4px'
      }}>
        <h2 style={{ color: '#2c3e50', marginTop: 0, fontSize: '18px', fontWeight: 600 }}>Payment options</h2>
        <ul style={{ color: '#333', fontSize: '16px', lineHeight: 1.8, paddingLeft: '20px', margin: 0 }}>
          <li><strong>Cash or cheque:</strong> pay at the registrar's office</li>
          <li><strong>E-transfer:</strong> registraruccma@gmail.com</li>
          <li><strong>Credit card:</strong> use Pay Online below</li>
        </ul>
        {balanceDue >= 10 && registration.id && (
          <div className="invoice-pay-section" style={{ marginTop: '20px', textAlign: 'center' }}>
            <a
              href={`/pay/${registration.id}`}
              onClick={(e) => {
                e.preventDefault();
                navigate(`/pay/${registration.id}`);
              }}
              style={{
                padding: '12px 24px',
                fontSize: '16px',
                backgroundColor: '#4299e1',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 600,
                textDecoration: 'none',
                display: 'inline-block'
              }}
            >
              Pay Online
            </a>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="invoice-footer" style={{
        marginTop: '30px',
        paddingTop: '20px',
        borderTop: '1px solid #dee2e6'
      }}>
        <p style={{ fontSize: '16px', fontWeight: 'bold', color: '#000' }}>Thank you for registering for camp</p>
      </div>
    </div>
  );
};

export default InvoiceDisplay; 